import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const BannerSlideTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [slides, setSlides] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.bannerSlides || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchSlides = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/banner-slides');
            setSlides(data.slides);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load banner slides", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSlides();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/banner-slide/${id}`);
            enqueueSnackbar("Banner slide deleted", { variant: "success" });
            setSlides((prev) => prev.filter((s) => s._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete slide", { variant: "error" });
        }
    };

    const columns = [
        {
            field: "image",
            headerName: "Image",
            minWidth: 100,
            flex: 0.4,
            sortable: false,
            renderCell: (params) => (
                <img draggable="false" src={params.row.image} alt={params.row.heading} className="w-16 h-10 object-cover rounded" />
            ),
        },
        {
            field: "heading",
            headerName: "Title",
            minWidth: 200,
            flex: 1,
        },
        {
            field: "order",
            headerName: "Order",
            type: "number",
            minWidth: 80,
            flex: 0.3,
        },
        {
            field: "active",
            headerName: "Active",
            minWidth: 90,
            flex: 0.3,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Hidden</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"banner-slide"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.heading} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = slides.map((slide) => ({
        id: slide._id,
        heading: slide.title || slide.heading || "Untitled slide",
        image: slide.image?.url,
        order: slide.order,
        active: slide.active,
    }));

    return (
        <>
            <MetaData title="Admin: Banner Slides | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <Link to="/admin/site-content" className="text-sm text-primary-blue hover:underline">&larr; Back to Site Content</Link>
                    <h1 className="text-lg font-medium uppercase mt-1">Banner Slides</h1>
                </div>
                {canCreate && (
                    <Link to="/admin/banner-slide/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Slide</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 470 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default BannerSlideTable;